import React, { useState, useEffect } from "react";
import { useToast } from "../context/ToastContext";
import { useOutletContext } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { API_BASE_URL } from "../utils/config";
import MedicalCertificate from './MedicalCertificate';
import CandidateEducation from './CandidateEducation';
import {
  FiCreditCard, FiShield, FiUsers,
  FiUpload, FiCheckCircle, FiAlertTriangle, FiEye,
  FiSearch, FiX, FiRefreshCw, FiBriefcase, FiPhone,
  FiEdit, FiInbox, FiFile, FiAward, FiImage
} from "react-icons/fi";
import "./EmployeeDashboard.css";

const CandidatePersonalDocuments = () => {
  const { showToast } = useToast();
  const { personalDocs, fetchDashboardData, formatDocumentUrl, profile } = useOutletContext();
  const docs = personalDocs?.documents || personalDocs || {};
  const [uploading, setUploading] = useState({});
  const [localSearch, setLocalSearch] = useState("");

  // ✅ Helper: get best available auth header or candidateId
  const getAuthHeaders = () => {
    const token = localStorage.getItem("candidateToken");
    if (token && token !== "null" && token !== "undefined") {
      return { Authorization: `Bearer ${token}` };
    }
    return {}; // fallback — candidateId will be sent in body
  };

  const getCandidateId = () => {
    return profile?._id || localStorage.getItem("candidateId") || null;
  };

  const [isBankModalOpen, setIsBankModalOpen] = useState(false);
  const [isEmergencyModalOpen, setIsEmergencyModalOpen] = useState(false);
  const [isPersonalModalOpen, setIsPersonalModalOpen] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [bankForm, setBankForm] = useState({ bankName: "", accountNumber: "", ifscCode: "" });
  const [emergencyForm, setEmergencyForm] = useState({ name: "", phone: "", relationship: "" });
  const [personalForm, setPersonalForm] = useState({ 
    name: "", phone: "", address: "", 
    dob: "", gender: "", city: "", state: "", pincode: ""
  });



  useEffect(() => {
    if (docs.bankDetails) {
      setBankForm({
        bankName: docs.bankDetails.bankName || "",
        accountNumber: docs.bankDetails.accountNumber || "",
        ifscCode: docs.bankDetails.ifscCode || ""
      });
    }
    if (docs.emergencyContact1) {
      setEmergencyForm({
        name: docs.emergencyContact1.name || "",
        phone: docs.emergencyContact1.phone || "",
        relationship: docs.emergencyContact1.relationship || ""
      });
    }
    if (profile) {
      setPersonalForm({
        name: profile.name || "",
        phone: profile.phone || "",
        address: profile.address || "",
        dob: profile.dob ? new Date(profile.dob).toISOString().split('T')[0] : "",
        gender: profile.gender || "",
        city: profile.city || "",
        state: profile.state || "",
        pincode: profile.pincode || ""
      });
    }
  }, [docs, profile]);

  const handleFileUpload = async (event, type) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploading({ ...uploading, [type]: true });
    const formData = new FormData();
    formData.append("document", file);
    formData.append("type", type);
    const candidateId = getCandidateId();
    if (candidateId) formData.append("candidateId", candidateId);
    try {
      await axios.post(`${API_BASE_URL}/candidate/documents/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      fetchDashboardData();
    } catch (err) {
      console.error(`Upload error for ${type}:`, err);
      showToast("Failed to upload document. Please make sure you are logged in.", "error");
    } finally {
      setUploading({ ...uploading, [type]: false });
    }
  };

  const handleSaveBankDetails = async (e) => {
    if (e) e.preventDefault();
    if (!bankForm.bankName || !bankForm.accountNumber || !bankForm.ifscCode) return showToast("All fields are required", "warning");
    setIsSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/candidate/save-bank-details`,
        { bankDetails: bankForm, candidateId: getCandidateId() },
        { headers: getAuthHeaders() }
      );
      showToast("Bank details saved successfully", "success");
      fetchDashboardData();
      setIsBankModalOpen(false);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to save bank details", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmergencyContact = async (e) => {
    if (e) e.preventDefault();
    if (!emergencyForm.name || !emergencyForm.phone || !emergencyForm.relationship) return showToast("All fields are required", "warning");
    setIsSaving(true);
    try {
      await axios.post(`${API_BASE_URL}/candidate/save-emergency-contact`,
        { contactNumber: 1, contact: emergencyForm, candidateId: getCandidateId() },
        { headers: getAuthHeaders() }
      );
      showToast("Emergency contact saved successfully", "success");
      fetchDashboardData();
      setIsEmergencyModalOpen(false);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to save contact", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleSavePersonalDetails = async (e) => {
    if (e) e.preventDefault();
    if (!personalForm.name || !personalForm.phone) return showToast("Name and Phone are required", "warning");
    setIsSaving(true);
    try {
      await axios.put(`${API_BASE_URL}/candidate/profile`,
        { ...personalForm, candidateId: getCandidateId() },
        { headers: getAuthHeaders() }
      );
      showToast("Personal details updated successfully", "success");
      fetchDashboardData();
      setIsPersonalModalOpen(false);
    } catch (err) {
      console.error(err);
      showToast(err.response?.data?.message || "Failed to update profile", "error");
    } finally {
      setIsSaving(false);
    }
  };

  const docCategories = [
    { title: "Aadhar / National ID", type: "aadharCard", icon: FiCreditCard, desc: "National Identity" },
    { title: "PAN Card", type: "panCard", icon: FiCreditCard, desc: "Tax Identity" },
    { title: "10th Certificate", type: "tenthCertificate", icon: FiFile, desc: "Educational Record" },
    { title: "12th Certificate", type: "twelfthCertificate", icon: FiFile, desc: "Educational Record" },
    { title: "Graduation Degree", type: "graduationCertificate", icon: FiAward, desc: "Higher Education" },
    { title: "Passport Photo", type: "passportPhoto", icon: FiImage, desc: "Identity Photo" }
  ];

  const filteredCategories = docCategories.filter(cat => 
    cat.title.toLowerCase().includes(localSearch.toLowerCase()) ||
    cat.desc.toLowerCase().includes(localSearch.toLowerCase())
  );

  return (
    <div className="emp-dash">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Dashboard Header */}
        <div className="emp-dash__header">
          <div>
            <h1 className="emp-dash__greeting">
              Personal <span>Documents</span>
            </h1>
            <p className="emp-dash__subtitle">
              Manage your identity and verification documents
            </p>
          </div>
          <div className="emp-dash__date-pill">
            <FiShield />
            <span>
              {new Date().toLocaleDateString("en-US", {
                weekday: "short",
                year: "numeric",
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
        </div>

        {/* Personal Details Section */}
        <div className="emp-dash__card mb-6">
          <div className="emp-dash__card-header">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600 flex items-center justify-center shadow-inner border border-indigo-200/50">
                <FiCreditCard size={24} />
              </div>
              <div>
                <h3 className="emp-dash__card-title">Personal Profile</h3>
                <p className="emp-dash__card-desc">Core identity & verification data</p>
              </div>
            </div>
            <button 
              onClick={() => setIsPersonalModalOpen(true)}
              className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-bold uppercase tracking-widest hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100 active:scale-95"
            >
              <FiEdit size={14} /> Edit Details
            </button>
          </div>
          <div className="emp-dash__card-body bg-gray-50/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-6">
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Full Name</p>
                <p className="text-sm font-bold text-gray-900 uppercase tracking-tight">{profile?.name || "Not set"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Phone Number</p>
                <p className="text-sm font-bold text-gray-900 tracking-tight">{profile?.phone || "Not set"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date of Birth</p>
                <p className="text-sm font-bold text-gray-900 tracking-tight">{profile?.dob ? new Date(profile.dob).toLocaleDateString() : "Not set"}</p>
              </div>
              <div className="space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Gender</p>
                <p className="text-sm font-bold text-gray-900 uppercase tracking-tight">{profile?.gender || "Not set"}</p>
              </div>
              <div className="lg:col-span-1 space-y-1">
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Current Address</p>
                <p className="text-sm font-bold text-gray-900 uppercase tracking-tight leading-relaxed">
                  {profile?.address ? `${profile.address}, ${profile.city || ""}, ${profile.state || ""} - ${profile.pincode || ""}` : "Not set"}
                </p>
              </div>
            </div>
          </div>
        </div>

      <CandidateEducation />
      <MedicalCertificate />

      {/* Personal Documents Section */}
      <div className="emp-dash__card mb-6">
        <div className="emp-dash__card-header">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-50 to-indigo-100 text-indigo-600 flex items-center justify-center shadow-inner border border-indigo-200/50">
              <FiShield size={24} />
            </div>
            <div>
              <h3 className="emp-dash__card-title">Personal Documents</h3>
              <p className="emp-dash__card-desc">Verification & banking vault</p>
            </div>
          </div>
        </div>


        <div className="emp-dash__card-body bg-gray-50/50">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            
            {/* Details Sidebar - Summary Cards */}
            <div className="lg:col-span-1 space-y-6">
            
            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 relative group transition-all hover:shadow-md md:min-h-[280px] flex flex-col justify-between">

              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center shadow-sm border border-blue-100/50">
                    <FiBriefcase size={18} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-800 uppercase tracking-widest">Bank Profile</h3>
                    <p className="text-[8px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5">Payroll Source</p>
                  </div>
                </div>
                <button onClick={() => setIsBankModalOpen(true)} className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-blue-600 hover:text-white text-gray-400 flex items-center justify-center transition-all shadow-sm border border-gray-100"><FiEdit size={12}/></button>
              </div>
              
              {docs.bankDetails?.accountNumber ? (
                <div className="space-y-3">
                   <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100/50">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Institution</p>
                      <p className="text-xs font-bold text-gray-800 mt-1 uppercase tracking-tight">{docs.bankDetails.bankName}</p>
                   </div>
                   <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100/50 font-mono">
                      <p className="text-[9px] font-bold text-gray-400 uppercase font-sans tracking-widest">A/C Number</p>
                      <p className="text-xs font-bold text-gray-800 mt-1 tracking-widest">*******{docs.bankDetails.accountNumber.slice(-4)}</p>
                   </div>
                </div>
              ) : (
                <div className="p-6 bg-amber-50/50 rounded-xl border border-amber-100/50 text-center">
                   <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm text-amber-500">
                      <FiAlertTriangle size={16} />
                   </div>
                   <p className="text-[9px] font-bold text-amber-600 uppercase tracking-widest leading-relaxed">Config Required</p>
                </div>
              )}
            </div>

            <div className="bg-white p-6 md:p-8 rounded-2xl shadow-sm border border-gray-100 relative group transition-all hover:shadow-md md:min-h-[280px] flex flex-col justify-between">

              <div className="flex items-center justify-between mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-emerald-50 text-emerald-600 flex items-center justify-center shadow-sm border border-emerald-100/50">
                    <FiUsers size={18} />
                  </div>
                  <div>
                    <h3 className="text-xs font-bold text-gray-800 uppercase tracking-widest">Emergency</h3>
                    <p className="text-[8px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-0.5">Primary Liaison</p>
                  </div>
                </div>
                <button onClick={() => setIsEmergencyModalOpen(true)} className="w-8 h-8 rounded-lg bg-gray-50 hover:bg-emerald-600 hover:text-white text-gray-400 flex items-center justify-center transition-all shadow-sm border border-gray-100"><FiEdit size={12}/></button>
              </div>
              
              {docs.emergencyContact1?.phone ? (
                <div className="space-y-3">
                   <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100/50">
                      <p className="text-[9px] font-bold text-gray-400 uppercase tracking-widest">Contact Person</p>
                      <p className="text-xs font-bold text-gray-800 mt-1 uppercase tracking-tight">{docs.emergencyContact1.name}</p>
                   </div>
                   <div className="p-4 bg-gray-50/50 rounded-xl border border-gray-100/50 flex items-center gap-3 font-mono">
                      <div className="w-7 h-7 bg-white rounded-lg flex items-center justify-center text-emerald-500 shadow-sm border border-emerald-50"><FiPhone size={10}/></div>
                      <p className="text-xs font-bold text-gray-800 tracking-widest">{docs.emergencyContact1.phone}</p>
                   </div>
                </div>
              ) : (
                <div className="p-6 bg-amber-50/50 rounded-xl border border-amber-100/50 text-center">
                   <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-sm text-amber-500">
                      <FiAlertTriangle size={16} />
                   </div>
                   <p className="text-[9px] font-bold text-amber-600 uppercase tracking-widest leading-relaxed">Config Required</p>
                </div>
              )}
            </div>
          </div>

          {/* Identity Documents Table */}
          <div className="lg:col-span-3">
            <div className="bg-white shadow-lg border border-gray-100 rounded-2xl md:rounded-[2.5rem] overflow-hidden">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 md:p-8 border-b border-gray-100 bg-gray-50/30 gap-4">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 md:w-12 md:h-12 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center shadow-sm border border-indigo-100/50">
                    <FiCreditCard size={20} className="md:w-6 md:h-6" />
                  </div>
                  <div>
                    <h2 className="text-xs md:text-sm font-bold text-gray-800 uppercase tracking-widest">Identity Documents</h2>
                    <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-1">Verification & Security Protocol</p>
                  </div>
                </div>
                <div className="flex items-center self-start sm:self-center gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-white border border-indigo-100 rounded-xl">
                   <div className="w-1.5 h-1.5 md:w-2 md:h-2 rounded-full bg-indigo-500 animate-pulse"></div>
                   <span className="text-[8px] md:text-[9px] font-bold text-indigo-600 uppercase tracking-widest">Secure Storage Active</span>
                </div>
              </div>

              <div className="overflow-x-auto custom-scrollbar">
                <table className="min-w-[800px] w-full text-center">
                  <thead className="text-[10px] text-gray-700 uppercase tracking-[0.2em] font-black border-b border-gray-200">
                    <tr>
                      <th className="py-4 md:py-5 px-4 md:px-8 text-left">Document Signature</th>
                      <th className="py-4 md:py-5 px-4 md:px-6 text-center">Verification Status</th>
                      <th className="py-4 md:py-5 px-4 md:px-6 text-center">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filteredCategories.map((doc, idx) => {
                      const docData = docs[doc.type];
                      const isUploaded = !!docData?.url;
                      return (
                        <tr key={idx} className="hover:bg-gray-50/50 transition-colors group">
                          <td className="py-5 px-8 text-left">
                            <div className="flex items-center gap-3 md:gap-4">
                              <div className={`w-8 h-8 md:w-10 md:h-10 rounded-xl flex items-center justify-center transition-all border shrink-0 ${isUploaded ? "bg-emerald-50 text-emerald-500 border-emerald-100 shadow-sm" : "bg-gray-50 text-gray-300 border-gray-100"}`}>
                                <doc.icon size={16} className="md:w-[18px] md:h-[18px]" />
                              </div>
                              <div className="min-w-0">
                                <h4 className="text-[11px] md:text-xs font-bold text-gray-900 uppercase tracking-widest truncate">{doc.title}</h4>
                                <p className="text-[8px] md:text-[9px] font-bold text-gray-400 uppercase tracking-widest mt-0.5 truncate">{doc.desc}</p>
                              </div>
                            </div>
                          </td>
                          <td className="py-5 px-6">
                            {isUploaded ? (
                              <div className="flex flex-col items-center gap-1">
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-emerald-600 text-[10px] font-bold uppercase tracking-widest border border-emerald-100 shadow-sm">
                                  <FiCheckCircle size={11} /> VERIFIED
                                </span>
                                {docData.uploadedAt && <span className="text-[8px] font-bold text-gray-400 uppercase tracking-widest">{new Date(docData.uploadedAt).toLocaleDateString("en-IN", { day: 'numeric', month: 'short' })}</span>}
                              </div>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 text-amber-500 text-[10px] font-bold uppercase tracking-widest border border-amber-100 shadow-sm">
                                <FiAlertTriangle size={11} /> MISSING
                              </span>
                            )}
                          </td>
                          <td className="py-4 md:py-5 px-4 md:px-6 text-center">
                            <div className="flex justify-center gap-2 md:gap-4">
                               {isUploaded ? (
                                 <>
                                   <a href={formatDocumentUrl(docData.url)} target="_blank" rel="noreferrer" className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-xl bg-indigo-50 text-indigo-700 hover:bg-indigo-600 hover:text-white border border-indigo-100 transition-all transform active:scale-90 shadow-sm" title="View Document">
                                     <FiEye size={14} className="md:w-4 md:h-4" />
                                   </a>
                                   <label className="w-8 h-8 md:w-10 md:h-10 flex items-center justify-center rounded-xl bg-gray-50 text-gray-400 hover:bg-blue-600 hover:text-white border border-gray-100 transition-all cursor-pointer active:scale-90 shadow-sm" title="Edit / Replace Document">
                                     {uploading[doc.type] ? <FiRefreshCw size={12} className="animate-spin text-indigo-500" /> : <FiEdit size={12} />}
                                     <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, doc.type)} accept=".pdf,.png,.jpg,.jpeg" />
                                   </label>
                                 </>
                               ) : (
                                 <label className="flex items-center gap-2 px-3 md:px-4 py-2 md:py-2.5 bg-gray-900 text-white rounded-xl text-[8px] md:text-[10px] font-bold uppercase tracking-widest transition-all hover:bg-black active:scale-95 shadow-lg border border-black cursor-pointer whitespace-nowrap" title="Upload Document">
                                   {uploading[doc.type] ? <FiRefreshCw size={11} className="animate-spin text-gray-400" /> : <FiUpload size={11} className="text-gray-400" />}
                                   <span>UPLOAD</span>
                                   <input type="file" className="hidden" onChange={(e) => handleFileUpload(e, doc.type)} accept=".pdf,.png,.jpg,.jpeg" />
                                 </label>
                               )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {filteredCategories.length === 0 && (
                <div className="p-20 text-center">
                  <div className="w-20 h-20 bg-gray-50 rounded-[2rem] flex items-center justify-center text-gray-200 mx-auto mb-6 border border-gray-100 shadow-inner"><FiInbox size={35} /></div>
                  <h3 className="text-base font-bold text-gray-700 uppercase tracking-widest">No Documents Found</h3>
                  <p className="text-[10px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-2">Adjust your search parameters</p>
                </div>
              )}
            </div>
          </div>
        </div>
        </div>
      </div>

      <div className="mt-8 flex items-center justify-center gap-3 text-[9px] font-bold text-gray-400 uppercase tracking-[0.2em]">
         <FiShield className="text-indigo-400" /> AES-256 Encryption Active • Timely Health Secure Storage
      </div>

      {/* Banking Modal */}
      <AnimatePresence>
        {isBankModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-md rounded-3xl md:rounded-[2.5rem] shadow-2xl p-6 md:p-10 border border-gray-100 relative max-h-[95vh] overflow-y-auto">
               <button className="absolute top-4 md:top-6 right-4 md:right-6 w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100" onClick={() => setIsBankModalOpen(false)}><FiX size={14}/></button>
               <div className="mb-6 md:mb-8 flex flex-col items-center text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-50 text-blue-500 rounded-2xl flex items-center justify-center mb-4 shadow-inner border border-blue-100/50"><FiBriefcase size={24} className="md:w-7 md:h-7"/></div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight uppercase tracking-widest">Banking Profile</h3>
                  <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Payroll & Financial Sync Protocol</p>
               </div>

               
               <form onSubmit={handleSaveBankDetails} className="space-y-5">
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Bank Institution</label>
                   <input type="text" value={bankForm.bankName} onChange={e => setBankForm({...bankForm, bankName: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none placeholder:text-gray-300" placeholder="e.g. HDFC Bank" />
                 </div>
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Account Number</label>
                   <input type="text" value={bankForm.accountNumber} onChange={e => setBankForm({...bankForm, accountNumber: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none tracking-widest placeholder:text-gray-300 placeholder:tracking-normal" placeholder="Enter account number" />
                 </div>
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">IFSC Identifier</label>
                   <input type="text" value={bankForm.ifscCode} onChange={e => setBankForm({...bankForm, ifscCode: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-blue-500 transition-all text-sm font-bold outline-none uppercase tracking-widest placeholder:text-gray-300 placeholder:tracking-normal" placeholder="HDFC0001234" />
                 </div>

                 <button type="submit" disabled={isSaving} className="w-full mt-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-700 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-blue-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50">
                   {isSaving ? "Transmitting..." : "Save Bank Profile"}
                 </button>
               </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Emergency Modal */}
      <AnimatePresence>
        {isEmergencyModalOpen && (
           <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-md rounded-3xl md:rounded-[2.5rem] shadow-2xl p-6 md:p-10 border border-gray-100 relative max-h-[95vh] overflow-y-auto">
               <button className="absolute top-4 md:top-6 right-4 md:right-6 w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100" onClick={() => setIsEmergencyModalOpen(false)}><FiX size={14}/></button>
               <div className="mb-6 md:mb-8 flex flex-col items-center text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-emerald-50 text-emerald-500 rounded-2xl flex items-center justify-center mb-4 shadow-inner border border-emerald-100/50"><FiUsers size={24} className="md:w-7 md:h-7"/></div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight uppercase tracking-widest">Emergency Contact</h3>
                  <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Primary Emergency Liaison Protocol</p>
               </div>

               
               <form onSubmit={handleSaveEmergencyContact} className="space-y-5">
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Contact Name</label>
                   <input type="text" value={emergencyForm.name} onChange={e => setEmergencyForm({...emergencyForm, name: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none placeholder:text-gray-300" placeholder="Enter full name" />
                 </div>
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                   <input type="text" value={emergencyForm.phone} onChange={e => setEmergencyForm({...emergencyForm, phone: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none tracking-widest placeholder:text-gray-300 placeholder:tracking-normal" placeholder="e.g. +91 9000000000" />
                 </div>
                 <div className="space-y-2">
                   <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Relationship</label>
                   <input type="text" value={emergencyForm.relationship} onChange={e => setEmergencyForm({...emergencyForm, relationship: e.target.value})} className="w-full px-5 py-3.5 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-emerald-500 transition-all text-sm font-bold outline-none placeholder:text-gray-300" placeholder="e.g. Spouse" />
                 </div>

                 <button type="submit" disabled={isSaving} className="w-full mt-8 py-4 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-emerald-100 hover:scale-[1.02] transition-all active:scale-95 disabled:opacity-50">
                   {isSaving ? "Transmitting..." : "Save Liaison Data"}
                 </button>
               </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Personal Details Modal */}
      <AnimatePresence>
        {isPersonalModalOpen && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md">
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} exit={{ opacity: 0, scale: 0.95 }} className="bg-white w-full max-w-2xl rounded-3xl md:rounded-[2.5rem] shadow-2xl p-6 md:p-10 border border-gray-100 relative max-h-[90vh] overflow-y-auto custom-scrollbar">
               <button className="absolute top-4 md:top-6 right-4 md:right-6 w-8 h-8 md:w-10 md:h-10 rounded-xl bg-gray-50 flex items-center justify-center text-gray-400 hover:text-red-500 transition-all shadow-sm border border-gray-100" onClick={() => setIsPersonalModalOpen(false)}><FiX size={14}/></button>
               <div className="mb-6 md:mb-8 flex flex-col items-center text-center">
                  <div className="w-14 h-14 md:w-16 md:h-16 bg-indigo-50 text-indigo-500 rounded-2xl flex items-center justify-center mb-4 shadow-inner border border-indigo-100/50"><FiCreditCard size={24} className="md:w-7 md:h-7"/></div>
                  <h3 className="text-lg md:text-xl font-bold text-gray-900 tracking-tight uppercase tracking-widest">Personal Details</h3>
                  <p className="text-[9px] md:text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-2">Candidate Profile Protocol</p>
               </div>

               
               <form onSubmit={handleSavePersonalDetails} className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Full Name</label>
                    <input type="text" value={personalForm.name} onChange={e => setPersonalForm({...personalForm, name: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="Enter full name" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Phone Number</label>
                    <input type="text" value={personalForm.phone} onChange={e => setPersonalForm({...personalForm, phone: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="+91 9000000000" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Date of Birth</label>
                    <input type="date" value={personalForm.dob} onChange={e => setPersonalForm({...personalForm, dob: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Gender</label>
                    <select value={personalForm.gender} onChange={e => setPersonalForm({...personalForm, gender: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none">
                      <option value="">Select Gender</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                      <option value="Prefer not to say">Prefer not to say</option>
                    </select>
                  </div>
                  <div className="md:col-span-2 space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Current Address</label>
                    <input type="text" value={personalForm.address} onChange={e => setPersonalForm({...personalForm, address: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="Street Address" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">City</label>
                    <input type="text" value={personalForm.city} onChange={e => setPersonalForm({...personalForm, city: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="City" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">State</label>
                    <input type="text" value={personalForm.state} onChange={e => setPersonalForm({...personalForm, state: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="State" />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold text-gray-400 uppercase tracking-widest ml-1">Pincode</label>
                    <input type="text" value={personalForm.pincode} onChange={e => setPersonalForm({...personalForm, pincode: e.target.value})} className="w-full px-5 py-3 bg-gray-50/50 border border-gray-200 rounded-2xl focus:bg-white focus:ring-2 focus:ring-indigo-500 transition-all text-sm font-bold outline-none" placeholder="Pincode" />
                  </div>

                  <div className="md:col-span-2 pt-4">
                    <button type="submit" disabled={isSaving} className="w-full py-4 bg-gradient-to-r from-indigo-600 to-purple-700 text-white rounded-2xl text-[10px] font-bold uppercase tracking-[0.2em] shadow-lg shadow-indigo-100 hover:scale-[1.01] transition-all active:scale-95 disabled:opacity-50">
                      {isSaving ? "Synchronizing..." : "Save Profile Details"}
                    </button>
                  </div>
                </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
      </div>
    </div>
  );
};

export default CandidatePersonalDocuments;